import KudosReason from './KudosReason';
import { Medal, Rocket, Trophy, Document } from 'assets/images';
import { usePathParam } from 'react-resource-router';
import { css } from '@compiled/react';
import { useLazyLoadQuery } from 'react-relay';
import { ProfileHeaderQuery } from 'graphql/__generated__/ProfileHeaderQuery.graphql';
import { PROFILE_HEADER_QUERY } from 'graphql/ProfileHeaderQuery';
import { AtlassianAccountAnyUserQuery } from 'graphql/__generated__/AtlassianAccountAnyUserQuery.graphql';
import { ATLASSIAN_ACCOUNT_ANY_USER_QUERY } from 'graphql/AtlassianAccountAnyUserQuery';
import { useCallback, useContext, useState } from 'react';
import { AppContext } from 'contexts/app';
import { getHighResolutionAvatar } from 'utils/helper';
import { IMAGE_SIZE } from 'constants/index';
import { token } from '@atlaskit/tokens';
import { MOBILE, TABLET } from '../../../../constants/breakpoints';

type KudosModalProps = {
  onReasonSelect: (reason: string) => void;
};

const KUDOS_COMMENTS = {
  GOING_ABOVE_BEYOND: 'Going above & beyond',
  WRITING_GREAT_CONTENT: 'Writing great content',
  BEING_AWESOME_AT_ACE: 'Being awesome at ACE',
  JUST_BECAUSE: 'Just because',
};

const KUDOS_IMAGES: Record<string, string> = {
  [KUDOS_COMMENTS.GOING_ABOVE_BEYOND]: Rocket,
  [KUDOS_COMMENTS.WRITING_GREAT_CONTENT]: Document,
  [KUDOS_COMMENTS.BEING_AWESOME_AT_ACE]: Trophy,
  [KUDOS_COMMENTS.JUST_BECAUSE]: Medal,
};

const KudosModalBody = ({ onReasonSelect }: KudosModalProps) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);

  const [pathProfileId] = usePathParam('pathProfileId');

  const profileResponseData = useLazyLoadQuery<ProfileHeaderQuery>(
    PROFILE_HEADER_QUERY,
    { internalId: pathProfileId as string },
    { fetchPolicy: 'store-or-network' },
  );

  const profileData = profileResponseData?.unified?.unifiedProfile;
  if (!profileData) {
    return <div>{'Could not fetch profile data'}</div>;
  }

  const userResponseData = useLazyLoadQuery<AtlassianAccountAnyUserQuery>(
    ATLASSIAN_ACCOUNT_ANY_USER_QUERY,
    { accountId: profileData.aaid as string },
    { fetchPolicy: 'store-or-network' },
  );
  const userData = userResponseData.user;
  if (!userData) {
    return <div>{'Could not fetch user data'}</div>;
  }

  const { picture } = userData;
  let highResolutionPicture = '';
  const { profileAvatar } = useContext(AppContext);
  highResolutionPicture = getHighResolutionAvatar(
    picture,
    IMAGE_SIZE.SMALL,
    profileAvatar,
  );

  const handleReasonClick = useCallback((reason: string) => {
    setSelectedReason(reason);
    onReasonSelect(reason); // Pass the reason up
  }, []);

  return (
    <div css={kudosModal}>
      <div css={profileSectionContainer}>
        <div css={profileSectionStyle}>
          <img
            css={profilePhotoStyles}
            src={highResolutionPicture}
            alt="Profile Photo"
          />
          <div
            css={[
              profileSectionText,
              !profileData?.role && profileSectionTextCentered,
            ]}
          >
            <p css={profileSectionName}>{`${userData?.nickname}`}</p>
            {profileData?.role && (
              <p css={profileSectionRole}>{profileData.role}</p>
            )}
          </div>
        </div>
      </div>

      <div css={recognitionReasonStyles}>
        <p css={subHeadReason}>{'Recognition reason'}</p>
        <div css={kudosReasonGrid}>
          {Object.values(KUDOS_COMMENTS).map((reason) => (
            <KudosReason
              key={reason}
              image={KUDOS_IMAGES[reason]}
              description={reason}
              onClick={() => handleReasonClick(reason)}
              selected={selectedReason === reason}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const subHeadReason = css({
  color: 'token("color.text")',
  fontFeatureSettings: '"lia" off, "clig" off',
  fontFamily: 'var(--Font-Family-Eyebrow, "Charlie Text")',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '16px',
  textTransform: 'uppercase',
});

const kudosReasonGrid = css({
  display: 'grid',
  width: '100%',
  gridTemplateColumns: '1fr 1fr',
  gap: '8px',
  alignItems: 'center',
  padding: '0 token("space.200")',
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    display: 'flex',
    flexDirection: 'column',
  },
});

const profilePhotoStyles = css({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
});

const profileSectionStyle = css({
  display: 'flex',
  padding: '12px 8px 14px 8px',
  alignItems: 'center',
  gap: '12px',
  alignSelf: 'stretch',
  borderRadius: '8px',
  border: `1px solid ${token('color.text.selected')}`,
});

const profileSectionText = css({
  fontFamily: 'Inter',
});

const profileSectionTextCentered = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const profileSectionName = css({
  fontSize: '16px',
  fontWeight: 400,
  color: '#292A2E',
  margin: 0,
});

const profileSectionRole = css({
  fontSize: '12px',
  color: '#505258',
  margin: 0,
});

const kudosModal = css({
  '&, *': {
    boxSizing: 'border-box',
  },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.200'),
  alignSelf: 'stretch',
});

const recognitionReasonStyles = css({
  display: 'flex',
  padding: '0px 16px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '6px',
  width: '100%',
  [TABLET.MEDIA_QUERY]: {
    margin: '0',
  },
});

const profileSectionContainer = css({
  display: 'flex',
  padding: '0px 16px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '32px',
  alignSelf: 'stretch',
});

export default KudosModalBody;
